'use client';

import { Button as AstryxButton } from '@astryxdesign/core/Button';
import { ReactNode, ButtonHTMLAttributes } from 'react';
import styles from './Button.module.css';

interface ButtonBaseProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'link' | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  loading?: boolean;
  children: ReactNode;
  className?: string;
}

interface ButtonAsButton extends ButtonBaseProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> {
  href?: never;
  target?: never;
  rel?: never;
}

interface ButtonAsLink extends ButtonBaseProps {
  href: string;
  target?: string;
  rel?: string;
  onClick?: never;
  disabled?: never;
  type?: never;
}

type ButtonProps = ButtonAsButton | ButtonAsLink;

/**
 * Button — legacy API over Astryx.
 *
 * Real `<button>`s render Astryx's Button (themed by shared/theme/hireos.ts);
 * the `href` and `link` forms keep the hand-rolled anchor/text rendering —
 * Astryx has no link-shaped Button, and navigation isn't a Button there.
 */
const ASTRYX_VARIANT = {
  primary: 'primary',
  secondary: 'secondary',
  danger: 'destructive',
  ghost: 'ghost',
  outline: 'outline', // custom variant defined in the hireos theme
} as const;

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  className = '',
  children,
  ...props
}: ButtonProps) {
  const legacyClasses = `${styles.link} ${className}`;

  // Render as <a> if href is provided (unchanged legacy path)
  if ('href' in props && props.href) {
    const { href, target, rel, ...rest } = props as ButtonAsLink;
    const linkClasses =
      variant === 'link'
        ? legacyClasses
        : `${styles.anchor} ${styles[variant]} ${styles[size]} ${className}`;
    return (
      <a href={href} target={target} rel={rel} className={linkClasses} {...rest as Record<string, unknown>}>
        {children}
      </a>
    );
  }

  const { disabled, type, onClick, 'aria-label': ariaLabel, ...buttonProps } = props as ButtonAsButton;

  if (variant === 'link') {
    return (
      <button type={type ?? 'button'} onClick={onClick} disabled={disabled || loading} className={legacyClasses} {...buttonProps}>
        {children}
      </button>
    );
  }

  const label = typeof children === 'string' ? children : (ariaLabel ?? '');

  return (
    <AstryxButton
      label={label}
      variant={ASTRYX_VARIANT[variant] as 'primary'}
      size={size === 'icon' ? 'sm' : size}
      isIconOnly={size === 'icon'}
      icon={size === 'icon' ? children : undefined}
      isLoading={loading}
      isDisabled={disabled}
      type={type}
      onClick={onClick}
      className={className}
      {...(buttonProps as Record<string, unknown>)}
    >
      {typeof children === 'string' || size === 'icon' ? undefined : (
        // Mixed children (an icon beside text) — see `.iconRow`.
        <span className={styles.iconRow}>{children}</span>
      )}
    </AstryxButton>
  );
}
